import { Check, ExternalLink, ShoppingBag } from 'lucide-react'
import { readableOn } from '../pages/customer/templates/shared'
import { t } from '../i18n'

// Ready-made palettes; the owner can also pick any color.
const PALETTES = [
  { name: 'Ocean', primary: '#0f766e', accent: '#f59e0b' },
  { name: 'Desert sand', primary: '#9a6b32', accent: '#1f2937' },
  { name: 'Date palm', primary: '#3f6212', accent: '#b45309' },
  { name: 'Royal blue', primary: '#1d4ed8', accent: '#f97316' },
  { name: 'Rose', primary: '#be185d', accent: '#7c3aed' },
  { name: 'Charcoal', primary: '#111827', accent: '#d97706' },
]
const HEX = /^#[0-9a-f]{6}$/i

// Contrast ratio between two #rrggbb colors (WCAG).
function contrast(a, b) {
  const lum = hex => {
    const n = parseInt(hex.slice(1), 16)
    return [n >> 16, (n >> 8) & 255, n & 255]
      .map(v => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4 })
      .reduce((sum, v, i) => sum + v * [0.2126, 0.7152, 0.0722][i], 0)
  }
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p)
  return (x + 0.05) / (y + 0.05)
}

function ColorField({ label, hint, value, onChange }) {
  const safe = HEX.test(value || '') ? value : '#000000'
  return (
    <div>
      <label className="block text-sm font-medium text-gray-800">{label}</label>
      <p className="text-xs text-gray-500">{hint}</p>
      <div className="mt-2 flex items-center gap-2">
        <input type="color" value={safe} onChange={e => onChange(e.target.value)} aria-label={label}
          className="h-11 w-14 cursor-pointer rounded-lg border border-gray-300 bg-white p-1" />
        <input type="text" value={value || ''} onChange={e => onChange(e.target.value.trim())} maxLength={7} dir="ltr"
          className={`form-input w-32 font-mono uppercase ${value && !HEX.test(value) ? 'border-red-400' : ''}`} placeholder="#0F766E" />
      </div>
    </div>
  )
}

export default function BrandColorsPanel({ values, onChange, onSave, saving, websiteUrl }) {
  const enabled = values.brand_colors_enabled === true
  const primary = HEX.test(values.primary_color || '') ? values.primary_color : '#0f766e'
  const accent = HEX.test(values.accent_color || '') ? values.accent_color : primary
  const onPrimary = readableOn(primary)
  const lowContrast = contrast(primary, '#ffffff') < 3 && contrast(primary, '#111111') < 3
  const invalid = (values.primary_color && !HEX.test(values.primary_color)) || (values.accent_color && !HEX.test(values.accent_color))

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-xl font-semibold text-gray-900">{t('Brand colors')}</h2>
        <p className="text-gray-600">{t('Give your website your own colors. Buttons, links, prices and highlights use them in every template.')}</p>
      </div>
      <div className="card-body space-y-6">
        <label className="flex items-start gap-3 rounded-xl border border-gray-200 p-4">
          <input type="checkbox" className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600" checked={enabled}
            onChange={e => onChange('brand_colors_enabled', e.target.checked)} />
          <span>
            <span className="block font-medium text-gray-900">{t('Use my brand colors on my website')}</span>
            <span className="block text-sm text-gray-500">{t("When this is off, your website uses the template's own colors.")}</span>
          </span>
        </label>

        <div className={`grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] ${enabled ? '' : 'pointer-events-none opacity-50'}`} aria-disabled={!enabled}>
          <div className="space-y-5">
            <div>
              <p className="text-sm font-medium text-gray-800">{t('Quick palettes')}</p>
              <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-6 lg:grid-cols-3 xl:grid-cols-6">
                {PALETTES.map(p => {
                  const active = primary.toLowerCase() === p.primary && accent.toLowerCase() === p.accent
                  return (
                    <button key={p.name} type="button" title={t(p.name)}
                      onClick={() => { onChange('primary_color', p.primary); onChange('accent_color', p.accent) }}
                      className={`relative flex h-12 overflow-hidden rounded-lg border-2 ${active ? 'border-gray-900' : 'border-transparent'}`}>
                      <span className="flex-[2]" style={{ background: p.primary }} />
                      <span className="flex-1" style={{ background: p.accent }} />
                      {active && <Check className="absolute inset-0 m-auto h-5 w-5 text-white drop-shadow" />}
                      <span className="sr-only">{t(p.name)}</span>
                    </button>
                  )
                })}
              </div>
            </div>
            <ColorField label={t('Main color')} hint={t('Buttons, links and your logo badge')} value={values.primary_color} onChange={v => onChange('primary_color', v)} />
            <ColorField label={t('Accent color')} hint={t('Small highlights and focus outlines')} value={values.accent_color} onChange={v => onChange('accent_color', v)} />
            {lowContrast && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">{t('This color is hard to read text on. Pick a darker or lighter shade.')}</p>}
          </div>

          {/* Live preview of the website with these colors */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-800">{t('Preview')}</p>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-[#faf9f6] shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold" style={{ background: primary, color: onPrimary }}>A</span>
                  <span className="font-semibold text-gray-900">{t('Your store')}</span>
                </div>
                <ShoppingBag className="h-5 w-5" style={{ color: primary }} />
              </div>
              <div className="space-y-3 p-4">
                <div className="flex gap-2">
                  <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: primary, color: onPrimary }}>{t('Popular')}</span>
                  <span className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700">{t('Drinks')}</span>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-white p-3 ring-1 ring-gray-200">
                  <div className="h-14 w-14 rounded-lg bg-gray-200" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">{t('Chicken Machboos')}</p>
                    <p className="text-sm font-semibold" style={{ color: primary }}>3.500</p>
                  </div>
                  <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
                </div>
                <button type="button" tabIndex={-1} className="w-full rounded-lg py-2.5 text-sm font-semibold" style={{ background: primary, color: onPrimary }}>{t('Add to order')}</button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4">
          {websiteUrl && <a href={websiteUrl} target="_blank" rel="noreferrer" className="btn-secondary inline-flex items-center gap-2"><ExternalLink className="h-4 w-4" />{t('View website')}</a>}
          <button type="button" onClick={onSave} disabled={saving || invalid} className="btn-primary">{saving ? t('Saving...') : t('Save colors')}</button>
        </div>
      </div>
    </div>
  )
}
